import type { AnyItem } from '#layers/data-model/types/schema/item';
import type { CompositePrimaryKey, PrimaryKey } from '#layers/data-model/types/schema/primary-key';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import { isDeepEqual } from '#layers/base/public';
import { pickByPrimitiveFields } from '#layers/data-model/utils/item';
import { getCollectionPrimaryKeyFields } from './schema/collection';

/**
 * Retrieves the primary key value(s) of the provided item from the specified collection.
 *
 * @param {AnyItem} item - The item from which to extract the primary key value(s).
 * @param {ResolvedMonospaceCollection} collection - The collection metadata containing information about primary key fields.
 * @return {PrimaryKey} The primary key value(s) for the provided item. For single-field primary keys, an object with a single property is returned. For composite primary keys, an object mapping field names to values is returned.
 */
export function getItemPrimaryKey(item: AnyItem, collection: ResolvedMonospaceCollection): CompositePrimaryKey {
	const pkFields = getCollectionPrimaryKeyFields(collection);

	if (pkFields.length === 0) {
		throw new Error('TODO');
	}

	return pickByPrimitiveFields(item, pkFields);
}

/**
 * Determines whether the provided key is a composite primary key.
 *
 * @param {PrimaryKey} key - The primary key to be checked.
 * @return {boolean} Returns true if the key is an object, otherwise false.
 */
export function isCompositePrimaryKey(key: PrimaryKey): key is CompositePrimaryKey {
	return typeof key === 'object';
}

export function toCompositePrimaryKey(key: PrimaryKey, collection: ResolvedMonospaceCollection): CompositePrimaryKey {
	if (isCompositePrimaryKey(key)) {
		return key;
	}

	const pkFields = getCollectionPrimaryKeyFields(collection);

	if (pkFields.length !== 1) {
		throw new Error('Received a primitive primary key, but collection has more than one primary key field');
	}

	return {
		[pkFields[0]!.apiName]: key,
	};
}

export function stringifyStableId(key: CompositePrimaryKey) {
	return JSON.stringify(key);
}

/**
 * Returns a string composed of item's primary key
 *
 * @param {AnyItem} item
 * @param {ResolvedMonospaceCollection} collection
 * @return {string} A string representation of the primary key encoded in JSON format.
 */
export function getItemPrimaryKeyString(item: AnyItem, collection: ResolvedMonospaceCollection): string {
	const primaryKey = getItemPrimaryKey(item, collection);
	return stringifyStableId(primaryKey);
}

/**
 * Parses a string representation of a primary key back into its original form.
 * Handles both primary key and composite primary keys.
 *
 * @param {string} keyString - The string representation of a primary key
 * @param {ResolvedMonospaceCollection} collection - The collection metadata
 * @return {CompositePrimaryKey} The parsed primary key in its original form (in object format)
 */
export function parseItemPrimaryKeyString(keyString: string, collection: ResolvedMonospaceCollection): CompositePrimaryKey {
	const pkFields = getCollectionPrimaryKeyFields(collection);

	if (pkFields.length === 0) {
		throw new Error('TODO');
	}

	try {
		const data = JSON.parse(keyString);

		// Check the fields
		if (!(typeof data === 'object') || Array.isArray(data)) {
			throw new TypeError('Invalid primary key format');
		}

		// Ensure all fields are present and in order
		const result: CompositePrimaryKey = {};

		for (const field of pkFields) {
			if (!(field.apiName in data)) {
				throw new Error(`Missing field ${field.apiName} in primary key`);
			}
			else {
				result[field.apiName] = data[field.apiName];
			}
		}

		return result;
	}
	catch {
		throw new Error('Invalid composite primary key format');
	}
}

/**
 * Generates a string representation of the primary key for an item, suitable for use in routes.
 */
export function getItemPrimaryKeyRouteString(item: AnyItem, collection: ResolvedMonospaceCollection): string {
	const pkFields = getCollectionPrimaryKeyFields(collection);

	if (pkFields.length === 0) {
		throw new Error('TODO');
	}

	if (pkFields.length === 1) {
		const field = pkFields[0]!;
		const value = item[field.apiName];
		return String(value);
	}

	return getItemPrimaryKeyString(item, collection);
}

/**
 * Generates a primary key object from a route string based on the collection's primary key fields.
 */
export function parseItemPrimaryKeyFromRouteString(routeString: string, collection: ResolvedMonospaceCollection): CompositePrimaryKey {
	const pkFields = getCollectionPrimaryKeyFields(collection);

	if (pkFields.length === 0) {
		throw new Error('TODO');
	}

	if (pkFields.length === 1) {
		const field = pkFields[0]!;
		const num = Number(routeString);
		const value = !Number.isNaN(num) && String(num) === routeString ? num : routeString;
		return { [field.apiName]: value };
	}

	return parseItemPrimaryKeyString(routeString, collection);
}

/**
 * Compares two primary key objects to check if they are equivalent.
 *
 * @param {PrimaryKey} key1 - The first primary key to be compared.
 * @param {PrimaryKey} key2 - The second primary key to be compared.
 * @return {boolean} Returns true if both primary keys are identical, otherwise false.
 */
export function isSameKey(key1: PrimaryKey, key2: PrimaryKey): boolean {
	return typeof key1 === 'object' && typeof key2 === 'object' ? isDeepEqual(key1, key2) : key1 === key2;
}

export function isPrimaryKeyComplete(key: CompositePrimaryKey, collection: ResolvedMonospaceCollection) {
	const pkFields = getCollectionPrimaryKeyFields(collection);

	for (const field of pkFields) {
		if (!(field.apiName in key)) {
			return false;
		}
	}

	return true;
}
